import NextDynamic from "next/dynamic";
import { MarqueeMedia } from "dds/organisms/marquee-media/marquee-media";
import { ParallaxMarquee } from "dds/organisms/parallax-marquee/parallax-marquee";
import { MarqueeParallaxVideo } from "dds/organisms/marquee-parallax-video/marquee-parallax-video";
import { VerticalSpacer } from "dds/layout/vertical-spacer/vertical-spacer";

/**
 * Blok type → DDS organism.
 *
 * The canvas spreads `component.props` — commonSbFixer's output — straight
 * into the organism, and none is needed while the blok is authored in the
 * organism's own prop names: every one of these publishes snake_case props
 * precisely because they are the CMS contract.
 *
 * EVERY VALUE HERE IS THE REAL DDS ORGANISM. No app wrapper, no stand-in: looking a
 * type up in this table tells you exactly what renders.
 *
 * ⚠ THREE TYPES NEED PROPS THIS TABLE CANNOT GIVE THEM. `DDS_GROW_CARD_CAROUSEL` and
 * `DDS_PLAN_CARD_CAROUSEL` expose a `zip_code` slot, `DDS_HP_TAB_CAROUSEL` a
 * `location_slot`, and all three take a React NODE that CMS JSON cannot carry. The
 * canvases fill them through `SLOT_FILLERS` (`slot-fillers.ts`) on the way in — which
 * is why those fillers import their organisms with `import type` ONLY. A value import
 * there would make the organism statically reachable from the canvas and collapse the
 * per-type split this table exists to create.
 *
 * `DDS_PRODUCT_RECOMMENDER` is different again: a page-adapter runs first and supplies
 * props the canvas cannot build. So does `DDS_HP_TAB_CAROUSEL`, via
 * `dds-hp-tab-carousel-adapter.ts` (cache-populate).
 * That now includes the vertical spacer, which lives under `layout/` but is
 * authored like an organism (`mobile_space`, `tablet_space`).
 *
 * Below-the-fold organisms load through `next/dynamic` at its DEFAULT
 * `ssr: true` — the option is not passed, because the default is what is
 * wanted. It is the framework's own mechanism for this, and it is the only
 * one of the three measured that splits PER PAGE, which is what matters as
 * the organism count grows toward the thirty-odd the DDS roadmap
 * implies.
 *
 * Measured three ways, every organism wired each time, over all four pages:
 *
 *   static          8 chunks  494 KB raw / 155 KB gzip   page chunk 94 / 32
 *   bare import()   8 chunks  483 KB raw / 151 KB gzip   page chunk 83 / 27
 *   next/dynamic    8 chunks  485 KB raw / 151 KB gzip   page chunk 84 / 27
 *
 * All three server-render identically: with JS never executed, every
 * below-the-fold organism's copy is in the HTML and so is the FAQ's
 * `itemType="…/FAQPage"`. Gzip is a dead heat with a bare `import()`.
 *
 * What differs is shape. This splits per page — 18-20 requests on first load
 * depending on how many organisms a page holds, against a flat 17 — and for
 * that it costs about 2 KB raw and a few KB at runtime, because a bare
 * `import()` takes code OUT of the client bundle where this DEFERS it.
 * Accepted deliberately: the per-page split is the property worth having.
 * Neither waterfalls; nothing is fetched after load in either.
 *
 * ⚠ THE SPLIT IS PER TYPE, AND ABOVE-THE-FOLD IS NOT. The canvas works out
 * what is above the fold from blok ORDER at render; this map is fixed at
 * build. So the eager set below is the organisms that are conventionally
 * first, not ones the code knows are. Put a lazy organism at the top of a
 * page and its chunk is on the critical path — an LCP regression rather than
 * a saving. Check the fixture order before adding a page.
 */

/** Hero organisms, conventionally first, plus anything with no client JS —
    neither is worth deferring. */
const EAGER = {
  DDS_MARQUEE_MEDIA: MarqueeMedia,
  DDS_PARALLAX_MARQUEE: ParallaxMarquee,
  DDS_MARQUEE_PARALLAX_VIDEO: MarqueeParallaxVideo,
  /* Not a hero, but an empty <div> with no client JS — a chunk of its own
     would cost a request to save nothing. */
  DDS_VERTICAL_SPACER: VerticalSpacer
} as const;

/**
 * Everything else.
 *
 * Chrome is not here — the layout imports GlobalNav, Footer and LinkFarm
 * directly. Neither is legal-modal: it is global, opened imperatively from a
 * legal link inside a text component, and never a blok in a page Body.
 */
const LAZY = {
  DDS_FAQ: NextDynamic(() => import("dds/organisms/faq/faq").then(m => m.Faq)),
  /* Its `zip_code` slot is filled by `fillPlanSlots` — see SLOT_FILLERS. */
  DDS_PLAN_CARD_CAROUSEL: NextDynamic(() => import(
    "dds/organisms/plan-card/plan-card-carousel"
  ).then(m => m.PlanCardCarousel)),
  DDS_CONTENT_CARD_CAROUSEL: NextDynamic(() => import(
    "dds/organisms/content-card-carousel/content-card-carousel"
  ).then(m => m.ContentCardCarousel)),
  /* Its `zip_code` slot is filled by `fillGrowSlots` — see SLOT_FILLERS. */
  DDS_GROW_CARD_CAROUSEL: NextDynamic(() => import(
    "dds/organisms/grow-card-carousel/grow-card-carousel"
  ).then(m => m.GrowCardCarousel)),
  DDS_IN_PAGE_LEGAL: NextDynamic(() => import(
    "dds/organisms/in-page-legal/in-page-legal"
  ).then(m => m.InPageLegal)),
  DDS_CENTER_BANNER: NextDynamic(() => import(
    "dds/organisms/center-banner/center-banner"
  ).then(m => m.CenterBanner)),
  /* Its `zipSlot` is filled by `fillLineupSlots` — see SLOT_FILLERS.

     ⚠ LAZY DESPITE BEING FIRST ON ITS PAGE: this organism is the whole page it appears on and
     appears on no other, so EAGER would put its client leaves into every v3 page's graph to save
     one chunk request on one page. The HTML is server-rendered either way. */
  DDS_CHANNEL_LINEUP: NextDynamic(() => import(
    "dds/organisms/channel-lineup/channel-lineup-section"
  ).then(m => m.ChannelLineupSection)),
  DDS_PAGE_SECTION_HEADER: NextDynamic(() => import(
    "dds/organisms/page-section-header/page-section-header"
  ).then(m => m.PageSectionHeader)),
  DDS_COMPARISON_TABLE: NextDynamic(() => import(
    "dds/organisms/comparison-table/comparison-table"
  ).then(m => m.ComparisonTable)),
  DDS_CONTENT_CARD_IMPACT: NextDynamic(() => import(
    "dds/organisms/content-card-impact/content-card-impact"
  ).then(m => m.ContentCardImpact)),
  /* ⚠ LAZY LIKE THE REST, AND THE TWO CARDS ARE THE RISK IN THAT: nothing
     stops a page authoring either one first, where its chunk lands on the
     critical path. Check the fixture order before putting one at the top. */
  DDS_CONTENT_CARD_LARGE: NextDynamic(() => import(
    "dds/organisms/content-card-large/content-card-large"
  ).then(m => m.ContentCardLarge)),
  DDS_CONTENT_CARD_SMALL: NextDynamic(() => import(
    "dds/organisms/content-card-small/content-card-small"
  ).then(m => m.ContentCardSmall)),
  DDS_CONTENT_RIBBON: NextDynamic(() => import(
    "dds/organisms/content-ribbon/content-ribbon"
  ).then(m => m.ContentRibbon)),
  DDS_DYNAMIC_RECOMMENDER: NextDynamic(() => import(
    "dds/organisms/dynamic-recommender/dynamic-recommender"
  ).then(m => m.DynamicRecommender)),
  /* Its `location_slot` is filled by `fillHpSlots` — see SLOT_FILLERS.
     ⚠ LAZY ON AN ASSUMPTION THAT NOTHING HAS CHECKED, and the header above says to
     check it: no page authors this blok yet, so there is no fixture order to read.
     The assumption is that it follows the opening — the homepage's own opening is the
     Parallax Marquee (`parallax-marquee.tsx`, "the homepage's scroll-driven
     opening"), and this section sits under it. Its own type calls it a "tabbed hero
     marquee" though, so if the first page to author it puts it at the TOP, move this
     to EAGER: a lazy organism on the critical path is an LCP regression, not a
     saving. Re-check when the first fixture lands. */
  DDS_HP_TAB_CAROUSEL: NextDynamic(() => import(
    "dds/organisms/hp-tab-carousel/hp-tab-carousel"
  ).then(m => m.HpTabCarousel)),
  /* `recipe` and `catalog` are built server-side by
     dds-product-recommender-adapter and arrive as props; the canvas itself
     supplies neither. ⚠ Still draws nothing until a recipe SOURCE is authored
     — the shell returns null without both, which is the intended hidden state
     while that CMS field is unbuilt. */
  DDS_PRODUCT_RECOMMENDER: NextDynamic(() => import(
    "dds/organisms/product-recommender/product-recommender"
  ).then(m => m.ProductRecommender)),
  /* ⚠ LAZY, AND DELIBERATELY SO EVEN THOUGH A DESCRIBER IS AUTHORED FIRST on
     every pair it labels — so on a sandbox page its chunk IS on the critical
     path, which the header above calls an LCP regression. It is accepted here
     because the only pages that author it are the sandbox ones: making it
     eager would put a tooling strip in the shared chunk of every marketing
     page instead, to spare the LCP of pages nobody but a reviewer opens. */
  DDS_CI_DESCRIBER: NextDynamic(() => import(
    "dds/organisms/ci-describer/ci-describer"
  ).then(m => m.CiDescriber))
} as const;

export const ORGANISM_MAP = {
  ...EAGER,
  ...LAZY
} as const;

export type OrganismType = keyof typeof ORGANISM_MAP;
